import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { DataClassification } from '@yada/types';
import { Lock, Cloud, AlertTriangle, ShieldAlert } from 'lucide-react';
import type { ConsentCallback, LlmLastError, LlmManager, WebLlmLoadingProgress } from './LlmManager';

interface ConsentRequest {
  appId: string;
  appName: string;
  classification: DataClassification;
  dataLabel?: string;
  providerLabel?: string;
  resolve: (granted: boolean) => void;
}

interface LlmConsentContextValue {
  pending: ConsentRequest | null;
  approve: () => void;
  deny: () => void;
}

const LlmConsentContext = createContext<LlmConsentContextValue>({
  pending: null,
  approve: () => {},
  deny: () => {},
});

interface Props {
  llmManager: LlmManager;
  children: React.ReactNode;
}

/**
 * LlmConsentProvider — renders the consent gate UI and wires the callback
 * into the LlmManager. Must wrap the app tree so the modal can appear over
 * any app content.
 *
 * Wires WebLLM download progress into the LLM manager. The active AI input
 * surface subscribes to that state and presents it in context.
 */
export function LlmConsentProvider({ llmManager, children }: Props) {
  const [pending, setPending] = useState<ConsentRequest | null>(null);
  const [llmError, setLlmError] = useState<LlmLastError | null>(null);
  // Queue to handle concurrent consent requests (rare but possible)
  const queue = useRef<ConsentRequest[]>([]);

  const processNext = useCallback(() => {
    const next = queue.current.shift();
    setPending(next ?? null);
  }, []);

  const consentCallback: ConsentCallback = useCallback((params) => {
    return new Promise<boolean>((resolve) => {
      const request: ConsentRequest = { ...params, resolve };
      if (!pending && queue.current.length === 0) {
        setPending(request);
      } else {
        queue.current.push(request);
      }
    });
  }, [pending]);

  // Wire consent callback into manager whenever it changes
  useEffect(() => {
    llmManager.setConsentCallback(consentCallback);
  }, [llmManager, consentCallback]);

  // Wire WebLLM download/load progress callback — splits into two phases via text heuristic.
  // Presentation belongs with the active AI request rather than a global popup.
  const seenLoadPhase = useRef(false);
  useEffect(() => {
    seenLoadPhase.current = false;
    llmManager.setWebLlmProgressCallback((p) => {
      const isDownload = /fetch|download|param cache/i.test(p.text);
      if (!isDownload) seenLoadPhase.current = true;

      const isDone = seenLoadPhase.current && p.progress >= 1;
      const state: WebLlmLoadingProgress = {
        downloadProgress: isDownload ? p.progress : 1,
        loadProgress: isDownload ? 0 : p.progress,
        text: p.text,
      };

      llmManager.setWebLlmLoadingState(isDone ? null : state);
      if (isDone) seenLoadPhase.current = false;
    });
    return () => {
      llmManager.setWebLlmProgressCallback(null);
      llmManager.setWebLlmLoadingState(null);
    };
  }, [llmManager]);

  // Subscribe to LLM errors — surface them as a dismissible toast so users know
  // when AI features fail and can take action (e.g. switch providers in Settings → AI).
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    const unsub = llmManager.onErrorChange((err) => {
      setLlmError(err);
      if (timer) clearTimeout(timer);
      if (err) {
        // Auto-dismiss after 8 seconds
        timer = setTimeout(() => setLlmError(null), 8000);
      }
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsub();
    };
  }, [llmManager]);

  const approve = useCallback(() => {
    pending?.resolve(true);
    processNext();
  }, [pending, processNext]);

  const deny = useCallback(() => {
    pending?.resolve(false);
    processNext();
  }, [pending, processNext]);

  return (
    <LlmConsentContext.Provider value={{ pending, approve, deny }}>
      {children}
      {pending && <LlmConsentModal />}
      {llmError && <LlmErrorToast error={llmError} onDismiss={() => setLlmError(null)} />}
    </LlmConsentContext.Provider>
  );
}

// ── LlmErrorToast ─────────────────────────────────────────────────────────────

/** Human-readable messages for each LLM error code. */
const ERROR_MESSAGES: Record<string, { icon: React.ReactNode; title: string; action: string }> = {
  'webllm-unavailable': {
    icon: <Lock size={14} />,
    title: 'Local AI unavailable',
    action: 'WebGPU is not supported on this browser. Switch to a remote provider in Settings → AI.',
  },
  'auth-error': {
    icon: <Cloud size={14} />,
    title: 'AI authentication failed',
    action: 'Your API key may be invalid or expired. Update it in Settings → AI.',
  },
  'network-error': {
    icon: <Cloud size={14} />,
    title: 'AI provider unreachable',
    action: 'Check your internet connection or try a different provider in Settings → AI.',
  },
  'provider-error': {
    icon: <AlertTriangle size={14} />,
    title: 'AI request failed',
    action: 'The AI provider returned an error. Check Settings → AI or try again.',
  },
  'local-only': {
    icon: <Lock size={14} />,
    title: 'Needs on-device AI',
    action: 'This feature uses content other people wrote, so it runs only on an on-device model. Turn on local AI in Settings → AI.',
  },
};

interface LlmErrorToastProps {
  error: LlmLastError;
  onDismiss: () => void;
}

function LlmErrorToast({ error, onDismiss }: LlmErrorToastProps) {
  const msg = ERROR_MESSAGES[error.code] ?? ERROR_MESSAGES['provider-error'];

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        position: 'fixed',
        bottom: '4.5rem',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        background: 'var(--panel)',
        border: '0.0625rem solid var(--border)',
        borderLeft: '0.25rem solid var(--error)',
        borderRadius: '0.5rem',
        padding: '0.75rem 1rem',
        maxWidth: '28rem',
        width: '90vw',
        boxShadow: '0 0.25rem 1.5rem rgba(0,0,0,0.5)',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        color: 'var(--text)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.25rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ color: 'var(--error, #ef4444)', display: 'flex', alignItems: 'center' }}>{msg.icon}</span>
        <span style={{ flex: 1, fontWeight: 600 }}>{msg.title}</span>
        <button
          onClick={onDismiss}
          aria-label="Dismiss AI error"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--muted)',
            fontSize: '1.125rem',
            lineHeight: 1,
            padding: 0,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
      <span style={{ color: 'var(--muted, #b0b6c4)' }}>{msg.action}</span>
    </div>
  );
}

function LlmConsentModal() {
  const { pending, approve, deny } = useContext(LlmConsentContext);
  if (!pending) return null;

  const classificationLabel: Record<DataClassification, string> = {
    'structured-anonymous': 'non-personal data',
    'personal-local': 'personal data (local only)',
    'personal-remote': 'personal data',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="llm-consent-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
      background: 'color-mix(in srgb, var(--bg) 50%, transparent)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--panel)',
        borderRadius: '0.75rem',
        padding: '1.5rem',
        maxWidth: '24rem',
        width: '90vw',
        boxShadow: '0 0.5rem 2rem rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', gap: '1rem',
      }}>
        <h2 id="llm-consent-title" style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
          Allow AI feature?
        </h2>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          <strong>{pending.appName}</strong> wants to send{' '}
          {pending.dataLabel
            ? <><strong>{pending.dataLabel}</strong> ({classificationLabel[pending.classification]})</>
            : classificationLabel[pending.classification]
          }{' '}to{' '}
          <strong>{pending.providerLabel ?? 'an external AI provider'}</strong>.
        </p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--muted)', lineHeight: 1.5 }}>
          {pending.classification === 'personal-remote' ? (
            <span style={{ display: 'flex', gap: '0.25rem', alignItems: 'flex-start' }}>
              <ShieldAlert size={13} style={{ flexShrink: 0, marginTop: '0.125rem', color: 'var(--warning)' }} />
              <span>
                This data will leave your device and be processed by an external AI provider.
                The provider may retain it per their own privacy policy.{' '}
              </span>
            </span>
          ) : (
            'This data will be sent over the network for processing. '
          )}
          Your approval covers all future requests from this app.
          To review or revoke AI consents, go to{' '}
          <strong>Settings → Data &amp; Privacy → AI Data Access</strong>.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button
            onClick={deny}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: '0.0625rem solid var(--border)',
              background: 'transparent', cursor: 'pointer', fontSize: '0.875rem',
              color: 'var(--text)',
            }}
          >
            Don't allow
          </button>
          <button
            onClick={approve}
            style={{
              padding: '0.5rem 1rem', borderRadius: '0.375rem',
              border: 'none',
              background: 'var(--accent)', color: 'var(--text-on-accent)',
              cursor: 'pointer', fontSize: '0.875rem',
            }}
          >
            Allow this app
          </button>
        </div>
      </div>
    </div>
  );
}
